import { animate, state, style, transition, trigger } from '@angular/animations';
import { AfterViewInit, Component, input, output, signal, ViewChild, effect, computed, OnDestroy } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatPaginator } from '@angular/material/paginator';import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatMenuModule } from '@angular/material/menu';
import { MatChipsModule } from '@angular/material/chips';
import { FormsModule } from '@angular/forms';
import { Observable, Subscription } from 'rxjs';
import { ICaseWorkbenchTask, IDashboardTask, TaskGroupHeaderRow, TaskTableRow, UWBaseProps } from '@Underwriting/models';
import { CaseWorkbenchTaskStatuses } from '@CaseSummary/underwriting/types';
import moment, { Moment } from 'moment';
import { SingleSelectDropdownListComponent, DropdownOption } from '@Components/single-select-dropdown-list/single-select-dropdown-list.component';
import UWTaskNotesPanel from '../uw-task-notes-panel/uw-task-notes-panel.component';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MAT_MOMENT_DATE_FORMATS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { CaseSummaryService, NotificationService, NotificationSeverity, UWTaskService } from '@Services';
import { MaskDateDirective } from '@App/directives';
import { TaskNamePipe } from '@App/pipes/task-name.pipe';
import { FollowUpDialog, SubtitleDialog } from '@CaseSummary/underwriting/components/requirements';
import { MatDialog } from '@angular/material/dialog';


function isGroupHeaderRow(row: TaskTableRow): row is TaskGroupHeaderRow {
  return !!(row as TaskGroupHeaderRow).isGroupHeader;
}

function isDashboardTask(row: TaskTableRow): row is IDashboardTask {
  return !isGroupHeaderRow(row) && 'caseInfo' in row;
}

@Component({
  selector: 'uw-tasks-list',
  standalone: true,
  imports: [
    FollowUpDialog, SubtitleDialog, CommonModule, MatTableModule, MatSortModule, MatIconModule, MatSelectModule, MatFormFieldModule, MatInputModule, 
    MatDatepickerModule, MatButtonModule, MatTooltipModule, MatMenuModule, MatChipsModule, FormsModule, SingleSelectDropdownListComponent, UWTaskNotesPanel, MaskDateDirective, TaskNamePipe
  ],
  providers: [
    DatePipe,
    { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS] },
    { provide: MAT_DATE_FORMATS, useValue: MAT_MOMENT_DATE_FORMATS },
    { provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { useUtc: true } }
  ],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
  templateUrl: './uw-tasks-list.component.html',
  styleUrls: ['./uw-tasks-list.component.scss']
})
export default class UWTasksList implements AfterViewInit, OnDestroy {
  @ViewChild(MatSort) sort: MatSort | undefined;
  
  tasks = input<(ICaseWorkbenchTask | IDashboardTask)[]>([]);
  users = input<any[]>([]);
  statuses = input<string[]>([]);
  readonly = input<boolean>(false);
  baseProps = input<UWBaseProps>();
  displayMode = input<'default' | 'dashboard'>('default');
  groupByCase = input<boolean>(false);
  currentUserId = input<string | undefined>(undefined);
  paginatorRef = input<MatPaginator | undefined>(undefined);
  loadAssignableUsers = input<(accountId: string, caseId: string) => Observable<DropdownOption[]>>();
  
  deleteTask = output<string>();
  updateTask = output<{ task: ICaseWorkbenchTask, message: string }>();
  taskReassigned = output<string>();

  minDate: Moment | undefined;
  maxDate: Moment | undefined;

  taskCompletedStatus = CaseWorkbenchTaskStatuses.Completed;
  taskOpenStatus = CaseWorkbenchTaskStatuses.Open;
  taskRemovedStatus = CaseWorkbenchTaskStatuses.Removed;

  expandedTask: ICaseWorkbenchTask | null = null;

  readonly expandedCaseGroups = signal<Set<string>>(new Set());
  readonly assignableTaskUsersCache = signal<Record<string, DropdownOption[]>>({});
  private readonly pendingCaseLoads = new Set<string>();
  private pageIndex = 0;
  private pageSize = 10;
  private wasGrouped = false;
  private pageSubscription?: Subscription;

  readonly defaultColumns = ['expand', 'status', 'name', 'assignedTo', 'addedDate', 'completedDate', 'followUpDate', 'delete'];
  readonly dashboardColumns = ['expand', 'name', 'caseNumber', 'policyNumber', 'assignedTo', 'addedDate', 'followUpDate', 'caseLink'];

  displayedColumns = computed(() =>
    this.displayMode() === 'dashboard' ? this.dashboardColumns : this.defaultColumns
  );

  tableRows(): TaskTableRow[] {
    const tasks = this.tasks();
    if (this.displayMode() !== 'dashboard' || !this.groupByCase()) {
      return tasks;
    }
    const groups = new Map<string, IDashboardTask[]>();
    for (const t of tasks as IDashboardTask[]) {
      const key = t.caseInfo?.caseNumberDisplay ?? '';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(t);
    }

    const sortedGroups = Array.from(groups.entries()).sort((a, b) => a[0].localeCompare(b[0]));

    const lastPage = Math.max(0, Math.ceil(sortedGroups.length / this.pageSize) - 1);
    const pageIndex = Math.min(this.pageIndex, lastPage);
    const start = pageIndex * this.pageSize;
    const pageGroups = sortedGroups.slice(start, start + this.pageSize);

    const rows: TaskTableRow[] = [];
    const expandedGroups = this.expandedCaseGroups();
    for (const [caseNumber, groupTasks] of pageGroups) {
      rows.push({ isGroupHeader: true, caseNumberDisplay: caseNumber, casePolicyNumber: groupTasks[0]?.caseInfo?.casePolicyNumber });
      if (expandedGroups.has(caseNumber)) {
        rows.push(...groupTasks);
      }
    }
    return rows;
  }

  readonly tableDataSource = new MatTableDataSource<TaskTableRow>([]);

  userOptions = computed<DropdownOption[]>(() => {
    return this.users().map(user => ({
      value: user.id,
      displayName: `${user.firstName} ${user.lastName}`
    }));
  });

  isTaskRow = (_index: number, row: TaskTableRow): boolean => !isGroupHeaderRow(row) && this.isTaskRowVisible(row as ICaseWorkbenchTask);
  isGroupRow = (_index: number, row: TaskTableRow): boolean => isGroupHeaderRow(row);

  asDashboardTask(row: TaskTableRow): IDashboardTask {
    return row as IDashboardTask;
  }

  constructor(
    public readonly notificationService: NotificationService,
    private readonly caseSummaryService: CaseSummaryService,    
    private readonly uwTaskService: UWTaskService,
    private readonly dialog: MatDialog
  ) {
    effect(() => {
      this.tableDataSource.data = this.tableRows();
    });

    effect(() => {
      const paginator = this.paginatorRef();
      const grouped = this.displayMode() === 'dashboard' && this.groupByCase();

      this.pageSubscription?.unsubscribe();
      this.pageSubscription = undefined;

      if (paginator && grouped) {
        if (!this.wasGrouped) {
          paginator.firstPage();
        }
        this.tableDataSource.paginator = null;
        this.pageIndex = paginator.pageIndex;
        this.pageSize = paginator.pageSize || 10;
        this.pageSubscription = paginator.page.subscribe(event => {
          this.pageIndex = event.pageIndex;
          this.pageSize = event.pageSize;
          this.tableDataSource.data = this.tableRows();
        });
      } else {
        this.tableDataSource.paginator = paginator ?? null;
      }

      this.wasGrouped = grouped;
    });

    effect(() => {
      if (this.displayMode() === 'dashboard' && this.groupByCase()) {
        const tasks = this.tasks() as IDashboardTask[];
        const caseNumbers = new Set<string>();
        tasks.forEach(t => {
          const caseNum = t.caseInfo?.caseNumberDisplay ?? '';
          if (caseNum) {
            caseNumbers.add(caseNum);
          }
        });
        this.expandedCaseGroups.set(caseNumbers);
      }
    });

    this.minDate = moment().startOf('day');
    this.maxDate = moment().add(12, 'month').startOf('day');

    effect(() => {
      if (this.displayMode() !== 'dashboard') return;
      const loader = this.loadAssignableUsers();
      if (!loader) {
        return;
      }

      const tasks = this.tasks() as IDashboardTask[];
      const cache = this.assignableTaskUsersCache();
      tasks.forEach(task => this.loadAssignableUsersForTask(task, cache, loader));
    });
  }

  private loadAssignableUsersForTask(
    task: IDashboardTask,
    cache: Record<string, DropdownOption[]>,
    loader: (accountId: string, caseId: string) => Observable<DropdownOption[]>
  ): void {
    const caseId = task.caseInfo?.caseId;
    const accountId = task.caseInfo?.accountId;
    if (!task.id || !accountId || !caseId) return;
    if (cache[task.id] || this.pendingCaseLoads.has(caseId)) return;

    this.pendingCaseLoads.add(caseId);
    loader(accountId, caseId).subscribe(options => this.onAssignableUsersLoaded(caseId, options));
  }

  private onAssignableUsersLoaded(caseId: string, options: DropdownOption[]): void {
    this.pendingCaseLoads.delete(caseId);
    const cacheUpdate: Record<string, DropdownOption[]> = {};
    for (const t of this.tasks() as IDashboardTask[]) {
      if (t.caseInfo?.caseId === caseId && t.id) {
        cacheUpdate[t.id] = options;
      }
    }
    this.assignableTaskUsersCache.update(c => ({ ...c, ...cacheUpdate }));
  }

  ngAfterViewInit(): void {
    if (this.sort) {
      this.initializeTableSorting();
    }
  }

  ngOnDestroy(): void {
    this.pageSubscription?.unsubscribe();
  }

  onStatusChange(task: ICaseWorkbenchTask, value: string) {
    if (this.readonly()) return;
    const statusName = (Object.entries(CaseWorkbenchTaskStatuses).find(([, v]) => v === value)?.[0]) ?? value;
    const message = `Task ${task.name} status updated to ${statusName}.`;
    this.updateTask.emit({ task: { ...task, statusId: value }, message });
  }

  onAssignedToChange(task: ICaseWorkbenchTask, value: string) {
    if (this.readonly()) return;
    const displayName = this.resolveAssigneeName(task.id, value);
    const message = displayName
      ? `Task ${task.name} reassigned to ${displayName}.`
      : `Task ${task.name} unassigned.`;
    this.updateTask.emit({ task: { ...task, assigneeId: value }, message });

    if (this.displayMode() === 'dashboard' && this.currentUserId() && value !== this.currentUserId() && task.id) {
      this.taskReassigned.emit(task.id);
    }
  }

  private resolveAssigneeName(taskId: string, userId: string): string | null {
    if (!userId) return null;
    const fromUsers = this.users().find((u: any) => u.id === userId);
    if (fromUsers) return `${fromUsers.firstName} ${fromUsers.lastName}`;
    const fromCache = this.assignableTaskUsersCache()[taskId]?.find(o => o.value === userId);
    return fromCache?.displayName ?? null;
  }

  onFollowUpDateChange(task: ICaseWorkbenchTask, value: any) {
    if (this.readonly()) return;

    if (value !== null && value !== undefined) {
    const date = moment.utc(value).startOf('day');
    if (!date.isValid() || date.isBefore(this.minDate) || date.isAfter(this.maxDate)) {
      this.notificationService.showNotification({
        severity: NotificationSeverity.Error,
        message: `Follow-up date must be between ${moment(this.minDate).format('M/D/YYYY')} and ${moment(this.maxDate).format('M/D/YYYY')}.`
      });
      return;
    }
  }
    const followUpDate = value ? moment.utc(value).format('YYYY-MM-DD') : null;
    const formattedDate = followUpDate ? moment.utc(followUpDate).format('M/D/YYYY') : null;
    const message = formattedDate
      ? `Follow-up date for ${task.name} updated to ${formattedDate}.`
      : `Follow-up date for ${task.name} cleared.`;
    this.updateTask.emit({ task: { ...task, followUpDate }, message });
  }

  onDeleteTask(task: ICaseWorkbenchTask) {
    if (this.readonly()) return;
    this.onStatusChange(task, this.taskRemovedStatus);
  }

  toggleTaskOpenStatus(task: ICaseWorkbenchTask) {
    if (this.readonly()) return;  
    this.onStatusChange(task, this.taskOpenStatus);
  }

  toggleTaskCompletedStatus(task: ICaseWorkbenchTask) {
    if (this.readonly()) return;  
    this.onStatusChange(task, this.taskCompletedStatus);
  }

  toggleExpand(task: ICaseWorkbenchTask, event: Event) {
    event.stopPropagation();
    this.expandedTask = this.expandedTask === task ? null : task;
  }

  toggleCaseGroupExpand(caseNumber: string) {
    this.expandedCaseGroups.update(groups => {
      const newGroups = new Set(groups);
      if (newGroups.has(caseNumber)) {
        newGroups.delete(caseNumber);
      } else {
        newGroups.add(caseNumber);
      }
      return newGroups;
    });
  }

  isCaseGroupExpanded(caseNumber: string): boolean {
    return this.expandedCaseGroups().has(caseNumber);
  }

  private getCaseNumberForTask(task: ICaseWorkbenchTask): string {
    if (this.displayMode() === 'dashboard') {
      return (task as IDashboardTask).caseInfo?.caseNumberDisplay ?? '';
    }
    return '';
  }

  isTaskRowVisible(task: ICaseWorkbenchTask): boolean {
    if (this.displayMode() !== 'dashboard' || !this.groupByCase()) {
      return true;
    }
    const caseNumber = this.getCaseNumberForTask(task);
    return this.isCaseGroupExpanded(caseNumber);
  }

  openCase(task: IDashboardTask, event: Event) {
    event.stopPropagation();
    this.caseSummaryService.routeToCase({
      clientCode: task.caseInfo?.clientCode,
      accountCode: task.caseInfo?.clientCode + '-' + task.caseInfo?.accountCode,
      accountVersionNumber: task.caseInfo?.accountVersionNumber,
      caseId: task.caseInfo?.caseId
    }, true);
  }

  private initializeTableSorting(): void {
    this.tableDataSource.sortingDataAccessor = (row, column) => this.getRowSortValue(row, column);
    this.tableDataSource.sortData = (data, sort) => this.sortTableRows(data, sort);
    this.tableDataSource.sort = this.sort;
    if (this.displayMode() === 'dashboard') {
      this.sort.sort({ id: 'followUpDate', start: 'asc', disableClear: false });
    }
  }

  private getRowSortValue(row: TaskTableRow, column: string): string | number {
    if (isGroupHeaderRow(row)) return '';
    if (isDashboardTask(row)) {
      switch (column) {
        case 'caseNumber':   return row.caseInfo?.caseNumberDisplay ?? '';
        case 'policyNumber': return row.caseInfo?.casePolicyNumber ?? '';
        case 'addedDate':    return row.creationDate ?? '';
        case 'followUpDate': return row.followUpDate ?? '';
        case 'name':         return row.name ?? '';
        case 'assignedTo':   return row.assignedTo ?? '';
        default:             return (row as unknown as Record<string, unknown>)[column] as string ?? '';
      }
    }
    return (row as unknown as Record<string, unknown>)[column] as string ?? '';
  }

  private sortTableRows(data: TaskTableRow[], sort: MatSort): TaskTableRow[] {
    if (!sort.active || sort.direction === '') return data;

    const compare = (a: TaskTableRow, b: TaskTableRow): number => {
      const valA = this.tableDataSource.sortingDataAccessor(a, sort.active);
      const valB = this.tableDataSource.sortingDataAccessor(b, sort.active);
      return this.compareValues(valA, valB, sort.active, sort.direction);
    };

    return this.groupByCase() ? this.sortWithinGroups(data, compare) : [...data].sort(compare);
  }

  private compareValues(valA: string | number, valB: string | number, column: string, direction: string): number {
    if (column === 'followUpDate') {
      const emptyResult = this.compareEmptyValues(valA, valB, direction);
      if (emptyResult !== null) return emptyResult;
    }
    const cmp = this.compareRawValues(valA, valB);
    return direction === 'asc' ? cmp : -cmp;
  }

  private compareEmptyValues(valA: string | number, valB: string | number, direction: string): number | null {
    const isEmptyA = !valA || valA === '';
    const isEmptyB = !valB || valB === '';
    if (isEmptyA === isEmptyB) return null;
    const emptyGoesLast = direction === 'asc' ? 1 : -1;
    return isEmptyA ? emptyGoesLast : -emptyGoesLast;
  }

  private compareRawValues(valA: string | number, valB: string | number): number {
    if (typeof valA === 'string' && typeof valB === 'string') {
      return valA.localeCompare(valB, undefined, { numeric: true, sensitivity: 'base' });
    }
    if (valA < valB) return -1;
    if (valA > valB) return 1;
    return 0;
  }

  private sortWithinGroups(data: TaskTableRow[], compare: (a: TaskTableRow, b: TaskTableRow) => number): TaskTableRow[] {
    const groupMap = new Map<string, TaskTableRow[]>();
    let currentCaseNumber: string | null = null;

    for (const row of data) {
      if (isGroupHeaderRow(row)) {
        currentCaseNumber = row.caseNumberDisplay;
        groupMap.set(currentCaseNumber, [row]);
      } else if (currentCaseNumber) {
        groupMap.get(currentCaseNumber)?.push(row);
      }
    }

    const sortedCaseNumbers = Array.from(groupMap.keys()).sort((a, b) => a.localeCompare(b));

    return sortedCaseNumbers.flatMap(caseNumber => {
      const [header, ...taskRows] = groupMap.get(caseNumber)!;
      return [header, ...taskRows.sort(compare)];
    });
  }
  
  openSubtitle(caseTask: ICaseWorkbenchTask) {
        const subtitleExists = caseTask.subtitle && caseTask.subtitle != null;
        const title = subtitleExists ? "Edit Subtitle" : "Add Subtitle";

        const dialogRef = this.dialog.open(SubtitleDialog, {
            width: '32rem',
            disableClose: true,
            data: {
                title,
                subtitle: caseTask.subtitle
            }
        });

        dialogRef.afterClosed().subscribe({
            next: (value: { subtitle: string }) => {
                caseTask.subtitle = value.subtitle;

                this.uwTaskService.updateUWTask(this.baseProps()?.case.id, caseTask.id, caseTask).subscribe();
            }
        });
    }

    openFollowup(caseTask: ICaseWorkbenchTask) {
        const followupExists = caseTask.followUpDate && caseTask.followUpDate != null;
        const title = followupExists ? "Edit Followup" : "Add Followup";

        const dialogRef = this.dialog.open(FollowUpDialog, {
            width: '32rem',
            disableClose: true,
            data: {
                title,
                followUp: caseTask.followUpDate,
                clientId: this.baseProps()?.case.clientId
            }
        });

        dialogRef.afterClosed().subscribe({
            next: (value: { followUp: string }) => {
                caseTask.followUpDate = value.followUp;

                this.uwTaskService.updateUWTask(this.baseProps()?.case.id, caseTask.id, caseTask).subscribe();
            }
        });
    }
}
